import { Button, Card, Checkbox, Form, Input, Select, notification, Space, Typography } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    useNotificationRecipientsQuery,
    useSendNotificationMutation,
} from '../../../components/notifications/queryHooks';

const { TextArea } = Input;

export default function AdminNotificationsPage() {
    const [form] = Form.useForm();
    const [targetAll, setTargetAll] = useState(false);
    const { data: recipients = [], isLoading: recipientsLoading } = useNotificationRecipientsQuery();
    const send = useSendNotificationMutation();

    const onFinish = async (values: {
        title: string;
        body: string;
        link?: string;
        user_uids?: string[];
    }) => {
        try {
            await send.mutateAsync({
                title: values.title,
                body: values.body,
                link: values.link,
                target_all: targetAll,
                user_uids: targetAll ? undefined : values.user_uids,
            });
            notification.success({ message: 'Notification sent' });
            form.resetFields();
            setTargetAll(false);
        } catch (err: unknown) {
            notification.error({
                message: 'Failed to send notification',
                description: err instanceof Error ? err.message : 'Unknown error',
            });
        }
    };

    return (
        <Card
            title="Send in-app notification"
            extra={<Link to="/admin">Back to admin</Link>}
            style={{ maxWidth: 720, margin: '24px auto' }}
        >
            <Typography.Paragraph type="secondary">
                Deliver a message to selected portal users or broadcast to everyone with an RBAC role assignment.
            </Typography.Paragraph>
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item
                    name="title"
                    label="Title"
                    rules={[{ required: true, message: 'Title is required' }]}
                >
                    <Input maxLength={200} showCount />
                </Form.Item>
                <Form.Item
                    name="body"
                    label="Message"
                    rules={[{ required: true, message: 'Message is required' }]}
                >
                    <TextArea rows={5} maxLength={10000} showCount />
                </Form.Item>
                <Form.Item name="link" label="Link (optional)">
                    <Input placeholder="/crm/campaigns or https://…" />
                </Form.Item>
                <Form.Item>
                    <Checkbox
                        checked={targetAll}
                        onChange={(e) => {
                            setTargetAll(e.target.checked);
                            if (e.target.checked) {
                                form.setFieldValue('user_uids', []);
                            }
                        }}
                    >
                        Send to all portal users
                    </Checkbox>
                </Form.Item>
                {!targetAll && (
                    <Form.Item
                        name="user_uids"
                        label="Recipients"
                        rules={[
                            {
                                validator: async (_, value: string[] | undefined) => {
                                    if (targetAll) return;
                                    if (value && value.length > 0) return;
                                    throw new Error('Select at least one user or use broadcast');
                                },
                            },
                        ]}
                    >
                        <Select
                            mode="multiple"
                            loading={recipientsLoading}
                            optionFilterProp="label"
                            placeholder="Select users"
                            options={recipients.map((r) => ({
                                value: r.uid,
                                label: r.email ? `${r.name} (${r.email})` : r.name,
                            }))}
                        />
                    </Form.Item>
                )}
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={send.isLoading}>
                            Send
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
}
